// La agenda: todos los partidos del torneo en una sola lista, de todas las
// categorías y fases, con lo que hace falta para mostrarlos por horario y para
// repartirlos. La arman igual la página pública y el panel del organizador.

import type { Categoria, Pareja } from './api';
import { textoDeLado } from '../components/Cuadro';
import { nombrePareja } from '../components/Grupos';
import { nombreDeRonda } from '../nucleo/cuadro';
import { aClave } from '../nucleo/texto';
import type { Marcador } from '../nucleo/marcador';
import type { PartidoARepartir, PartidoProgramado } from '../nucleo/programacion';
import { resolverCuadroGuardado } from '../nucleo/vistaCuadro';
import { conTabla } from '../nucleo/vistaGrupos';

export interface ItemAgenda {
  id: number;
  categoriaId: number;
  categoria: string;
  /** "Grupo A", "Cuartos", "Final" */
  fase: string;
  esCuadro: boolean;
  a: { texto: string; seSabe: boolean };
  b: { texto: string; seSabe: boolean };
  /** Las parejas que ya se saben. */
  parejas: number[];
  /** Los nombres de sus jugadores, normalizados: para detectar a quien juega en dos categorías. */
  jugadores: string[];
  marcador: Marcador | null;
  /** Tiene resultado cargado. */
  jugado: boolean;
  /** No se va a jugar: W.O. porque una pareja abandonó. */
  porAbandono: boolean;
  /** Pendiente y con las dos parejas sabidas: se puede marcar en juego. */
  sePuedeJugar: boolean;
  enJuego: boolean;
  dia: string | null;
  hora: string | null;
  cancha: number | null;
  /** Partidos que tienen que jugarse antes (para el asistente de horarios). */
  despuesDe: number[];
}

export function armarAgenda(categorias: readonly Categoria<Pareja>[]): ItemAgenda[] {
  const items: ItemAgenda[] = [];

  for (const c of categorias) {
    const parejas = new Map(c.parejas.map((p) => [p.id, p]));
    const jugadoresDe = (ids: number[]) =>
      ids.flatMap((id) => {
        const p = parejas.get(id);
        return p ? [aClave(p.jugador1), aClave(p.jugador2)].filter(Boolean) : [];
      });
    const bajas = new Set(c.parejas.filter((p) => p.estado === 'baja').map((p) => p.id));
    const pasan = c.pasan_por_grupo ?? 1;
    const idsDeGrupos: number[] = [];

    for (const g of c.grupos) {
      const vista = conTabla(g, bajas, pasan);
      for (const p of g.partidos) {
        if (p.pareja_a === null || p.pareja_b === null) continue;
        const r = vista.resultados.get(p.id);
        const jugado = p.estado === 'jugado';
        const porAbandono = r?.porAbandono ?? false;
        idsDeGrupos.push(p.id);
        items.push({
          id: p.id,
          categoriaId: c.id,
          categoria: c.nombre,
          fase: `Grupo ${g.letra}`,
          esCuadro: false,
          a: { texto: nombrePareja(parejas.get(p.pareja_a)), seSabe: true },
          b: { texto: nombrePareja(parejas.get(p.pareja_b)), seSabe: true },
          parejas: [p.pareja_a, p.pareja_b],
          jugadores: jugadoresDe([p.pareja_a, p.pareja_b]),
          marcador: r?.marcador ?? null,
          jugado,
          porAbandono,
          sePuedeJugar: !jugado && !porAbandono && !bajas.has(p.pareja_a) && !bajas.has(p.pareja_b),
          enJuego: !jugado && p.en_juego_desde !== null,
          dia: p.dia,
          hora: p.hora,
          cancha: p.cancha,
          despuesDe: [],
        });
      }
    }

    if (c.cuadro) {
      const cuadro = resolverCuadroGuardado(c.cuadro, c.grupos, bajas, pasan);
      const letras = new Map(c.grupos.map((g) => [g.id, g.letra]));
      const porRonda = cuadro.rondas.map((r) => r.length);
      cuadro.rondas.forEach((ronda, i) => {
        for (const x of ronda) {
          if (x.pasaDirecto) continue; // no se juega: no va a la agenda
          const fila = c.cuadro!.partidos.find((p) => p.id === x.id);
          if (!fila) continue;
          const jugado = fila.estado === 'jugado';
          const anteriores = i === 0 ? idsDeGrupos : [cuadro.rondas[i - 1]![x.pos * 2]!.id, cuadro.rondas[i - 1]![x.pos * 2 + 1]!.id];
          items.push({
            id: x.id,
            categoriaId: c.id,
            categoria: c.nombre,
            fase: nombreDeRonda(ronda.length),
            esCuadro: true,
            a: textoDeLado(x.a, parejas, letras, porRonda),
            b: textoDeLado(x.b, parejas, letras, porRonda),
            parejas: [x.a, x.b].flatMap((l) => ('pareja' in l ? [l.pareja] : [])),
            jugadores: jugadoresDe([x.a, x.b].flatMap((l) => ('pareja' in l ? [l.pareja] : []))),
            marcador: x.marcador,
            jugado,
            porAbandono: x.porAbandono,
            sePuedeJugar: !jugado && x.sePuedeCargar,
            enJuego: !jugado && fila.en_juego_desde !== null,
            dia: fila.dia,
            hora: fila.hora,
            cancha: fila.cancha,
            despuesDe: anteriores,
          });
        }
      });
    }
  }
  return items;
}

/** Por día, hora y cancha. Los que no tienen horario, al final. */
export function porHorario(a: ItemAgenda, b: ItemAgenda): number {
  return (
    (a.dia ?? '9').localeCompare(b.dia ?? '9') ||
    (a.hora ?? '').localeCompare(b.hora ?? '') ||
    (a.cancha ?? 99) - (b.cancha ?? 99) ||
    a.id - b.id
  );
}

export const aProgramado = (i: ItemAgenda): PartidoProgramado => ({
  id: i.id, dia: i.dia, hora: i.hora, cancha: i.cancha, parejas: i.parejas, jugadores: i.jugadores, jugado: i.jugado || i.porAbandono,
});

export const aRepartir = (i: ItemAgenda): PartidoARepartir => ({ id: i.id, parejas: i.parejas, jugadores: i.jugadores, despuesDe: i.despuesDe });

/** nombre normalizado -> como está escrito, para mostrar en los avisos. */
export function nombresDeJugadores(categorias: readonly Categoria<Pareja>[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const c of categorias) {
    for (const p of c.parejas) {
      for (const n of [p.jugador1, p.jugador2]) if (!m.has(aClave(n))) m.set(aClave(n), n);
    }
  }
  return m;
}
